import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  adminPaymentLinkService,
  type AdminPaymentLink,
  type AdminPaymentMode,
} from '../../services/adminPaymentLinkService';
import PaymentSubscriptionsPanel from './components/PaymentSubscriptionsPanel';
import { clientService, type Client } from '../../services/clientService';
import { getAllServices, type Service } from '../../services/servicesStore';
import { currencyService } from '../../services/currencyService';

const STATUS_LABELS: Record<AdminPaymentLink['sessionStatus'], string> = {
  initiated: 'Créé',
  pending: 'En attente',
  paid: 'Payé',
  failed: 'Échoué',
  canceled: 'Annulé',
  expired: 'Expiré',
};

const STATUS_BADGES: Record<AdminPaymentLink['sessionStatus'], string> = {
  initiated: 'bg-slate-100 text-slate-700',
  pending: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-600',
  expired: 'bg-orange-100 text-orange-700',
};

const PAYMENT_MODE_LABELS: Record<AdminPaymentMode, string> = {
  one_time: 'Paiement unique',
  recurring: 'Récurrent',
};

const PAYMENT_MODE_BADGES: Record<AdminPaymentMode, string> = {
  one_time: 'bg-slate-100 text-slate-700',
  recurring: 'bg-indigo-100 text-indigo-700',
};

const buildDefaultValidUntil = () => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return date.toISOString().slice(0, 10);
};

const formatDate = (value?: string) => {
  if (!value) {
    return 'Non défini';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Non défini';
  }

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return 'Jamais';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Jamais';
  }

  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const copyToClipboard = async (value: string, label: string) => {
  if (!value) {
    toast.error(`${label} indisponible`);
    return;
  }

  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copié`);
  } catch (error) {
    console.error(error);
    toast.error(`Impossible de copier ${label.toLowerCase()}`);
  }
};

const formatLineItemsSummary = (lineItems: AdminPaymentLink['lineItems']) =>
  lineItems
    .map((item) => `${item.label} x${item.quantity}`)
    .join(' • ');

type PaymentLinkFormState = {
  selectedClientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientCompany: string;
  selectedServiceId: string;
  serviceName: string;
  amount: string;
  currencyCode: string;
  quoteNumber: string;
  quoteValidUntil: string;
  clientMessage: string;
  notes: string;
  sendEmail: boolean;
  upsellItems: PaymentLinkItemFormState[];
};

type PaymentLinkItemFormState = {
  id: string;
  label: string;
  quantity: string;
  unitAmount: string;
};

const createUpsellItem = (): PaymentLinkItemFormState => ({
  id: `upsell_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  label: '',
  quantity: '1',
  unitAmount: '',
});

const createInitialState = (currencyCode = 'EUR'): PaymentLinkFormState => ({
  selectedClientId: '',
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  clientCompany: '',
  selectedServiceId: '',
  serviceName: '',
  amount: '',
  currencyCode,
  quoteNumber: '',
  quoteValidUntil: buildDefaultValidUntil(),
  clientMessage: '',
  notes: '',
  sendEmail: true,
  upsellItems: [],
});

const PaymentLinksManager = () => {
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [paymentLinks, setPaymentLinks] = useState<AdminPaymentLink[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [form, setForm] = useState<PaymentLinkFormState>(() =>
    createInitialState(currencyService.getDisplayCurrencyCode())
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingKey, setIsSendingKey] = useState('');
  const [isDeletingKey, setIsDeletingKey] = useState('');
  const [lastCreatedLink, setLastCreatedLink] = useState<AdminPaymentLink | null>(null);
  const [activeWorkspace, setActiveWorkspace] = useState<'payments' | 'subscriptions'>(
    'payments'
  );

  const loadClientsOnly = async () => {
    const coreClients = await clientService.getClients(true);
    setClients(coreClients);
    return coreClients;
  };

  const loadData = async () => {
    setIsLoading(true);

    try {
      const [paymentLinksResult, clientsResult, servicesResult] = await Promise.allSettled([
        adminPaymentLinkService.getPaymentLinks(),
        loadClientsOnly(),
        getAllServices(),
      ]);

      if (paymentLinksResult.status === 'fulfilled') {
        setPaymentLinks(paymentLinksResult.value);
      } else {
        console.error(paymentLinksResult.reason);
        setPaymentLinks([]);
      }

      if (clientsResult.status === 'fulfilled') {
        setClients(clientsResult.value);
      } else {
        console.error(clientsResult.reason);
        setClients([]);
      }

      if (servicesResult.status === 'fulfilled') {
        setServices(servicesResult.value);
      } else {
        console.error(servicesResult.reason);
        setServices([]);
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erreur de chargement paiements');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);

  useEffect(() => {
    setForm((current) => {
      if (
        current.selectedClientId ||
        current.selectedServiceId ||
        current.amount ||
        current.currencyCode === displayCurrencyCode
      ) {
        return current;
      }

      return {
        ...current,
        currencyCode: displayCurrencyCode,
      };
    });
  }, [displayCurrencyCode]);

  const formatOriginalAmount = (amount: number, currencyCode: string) =>
    currencyService.formatCurrencyAmount(amount, currencyCode, {
      display: 'code',
    });

  const formatDisplayAmount = (amount: number, currencyCode: string) =>
    currencyService.formatAmountForDisplay(amount, currencyCode, {
      country: displayCountry,
      display: 'code',
    });

  const handleClientChange = (clientId: string) => {
    const client = clients.find((entry) => entry.id === clientId);
    if (!client) {
      setForm((current) => ({
        ...current,
        selectedClientId: '',
      }));
      return;
    }

    setForm((current) => ({
      ...current,
      selectedClientId: client.id,
      clientName: client.name,
      clientEmail: client.email,
      clientPhone: client.phone || '',
      clientCompany: client.company || '',
      selectedServiceId: current.selectedServiceId || client.serviceId || '',
      serviceName: current.serviceName || client.serviceName || '',
      amount:
        current.amount ||
        (client.totalAmount > 0
          ? String(client.totalAmount.toFixed(2))
          : ''),
      currencyCode: client.currencyCode || current.currencyCode || 'EUR',
    }));
  };

  const handleServiceChange = (serviceId: string) => {
    const service = services.find((entry) => entry.id === serviceId);
    if (!service) {
      setForm((current) => ({
        ...current,
        selectedServiceId: '',
      }));
      return;
    }

    setForm((current) => ({
      ...current,
      selectedServiceId: service.id,
      serviceName: service.name,
      amount:
        service.price > 0
          ? currencyService
              .convertAmount(
                service.price,
                'EUR',
                current.currencyCode || displayCurrencyCode
              )
              .toFixed(2)
          : current.amount,
    }));
  };

  const resetForm = () => {
    setForm(createInitialState(displayCurrencyCode));
  };

  const buildLineItemsFromForm = (
    state: PaymentLinkFormState,
    strict = false
  ): {
    items: Array<{
      id?: string;
      label: string;
      quantity: number;
      unitAmount: number;
      lineTotal: number;
    }>;
    totalAmount: number;
  } => {
    const items: Array<{
      id?: string;
      label: string;
      quantity: number;
      unitAmount: number;
      lineTotal: number;
    }> = [];

    const baseLabel = state.serviceName.trim();
    const baseAmountRaw = Number(state.amount || 0);
    const baseAmount =
      Number.isFinite(baseAmountRaw) && baseAmountRaw > 0
        ? Number(baseAmountRaw.toFixed(2))
        : 0;

    if (baseLabel || state.amount.trim()) {
      if (strict && !baseLabel) {
        throw new Error('Le produit principal doit avoir un libellé.');
      }

      if (strict && baseAmount <= 0) {
        throw new Error('Le prix du produit principal doit être supérieur à 0.');
      }

      if (baseLabel && baseAmount > 0) {
        items.push({
          id: 'item_main',
          label: baseLabel,
          quantity: 1,
          unitAmount: baseAmount,
          lineTotal: baseAmount,
        });
      }
    }

    state.upsellItems.forEach((upsell, index) => {
      const label = upsell.label.trim();
      const quantityRaw = Number(upsell.quantity || 1);
      const quantity =
        Number.isFinite(quantityRaw) && Math.trunc(quantityRaw) > 0
          ? Math.trunc(quantityRaw)
          : 1;
      const unitAmountRaw = Number(upsell.unitAmount || 0);
      const unitAmount =
        Number.isFinite(unitAmountRaw) && unitAmountRaw > 0
          ? Number(unitAmountRaw.toFixed(2))
          : 0;
      const hasAnyValue = Boolean(label || upsell.unitAmount.trim() || upsell.quantity.trim());

      if (!hasAnyValue) {
        return;
      }

      if (strict && !label) {
        throw new Error(`Upsell ${index + 1}: libellé obligatoire.`);
      }

      if (strict && unitAmount <= 0) {
        throw new Error(`Upsell ${index + 1}: le prix unitaire doit être supérieur à 0.`);
      }

      if (label && unitAmount > 0) {
        items.push({
          id: upsell.id,
          label,
          quantity,
          unitAmount,
          lineTotal: Number((unitAmount * quantity).toFixed(2)),
        });
      }
    });

    const totalAmount = Number(
      items.reduce((sum, entry) => sum + entry.lineTotal, 0).toFixed(2)
    );

    return {
      items,
      totalAmount,
    };
  };

  const computedDraft = buildLineItemsFromForm(form, false);

  const handleAddUpsellItem = () => {
    setForm((current) => ({
      ...current,
      upsellItems: [...current.upsellItems, createUpsellItem()],
    }));
  };

  const handleRemoveUpsellItem = (itemId: string) => {
    setForm((current) => ({
      ...current,
      upsellItems: current.upsellItems.filter((entry) => entry.id !== itemId),
    }));
  };

  const handleUpsellChange = (
    itemId: string,
    field: keyof Omit<PaymentLinkItemFormState, 'id'>,
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      upsellItems: current.upsellItems.map((entry) =>
        entry.id === itemId
          ? {
              ...entry,
              [field]: value,
            }
          : entry
      ),
    }));
  };

  const handleCreate = async () => {
    setIsSubmitting(true);

    try {
      const computed = buildLineItemsFromForm(form, true);

      if (computed.items.length === 0) {
        throw new Error('Ajoute au moins un produit dans le devis.');
      }

      const primaryItem = computed.items[0];
      const resolvedServiceName = form.serviceName.trim() || primaryItem.label;
      const payloadLineItems = computed.items.map((entry) => ({
        id: entry.id,
        label: entry.label,
        quantity: entry.quantity,
        unitAmount: entry.unitAmount,
      }));

      const result = await adminPaymentLinkService.createPaymentLink({
        clientId: form.selectedClientId || undefined,
        clientName: form.clientName.trim(),
        clientEmail: form.clientEmail.trim(),
        clientPhone: form.clientPhone || undefined,
        clientCompany: form.clientCompany || undefined,
        serviceId: form.selectedServiceId || undefined,
        serviceName: resolvedServiceName,
        amount: computed.totalAmount,
        lineItems: payloadLineItems,
        currencyCode: form.currencyCode,
        quoteNumber: form.quoteNumber || undefined,
        quoteValidUntil: form.quoteValidUntil || undefined,
        clientMessage: form.clientMessage || undefined,
        notes: form.notes || undefined,
        sendEmail: form.sendEmail,
        emailTemplateAlias: 'checkout-quote',
      });

      if (form.clientName.trim() && form.clientEmail.trim()) {
        try {
          await clientService.saveClient({
            id: form.selectedClientId || clientService.createEmptyClient().id,
            name: form.clientName.trim(),
            email: form.clientEmail.trim().toLowerCase(),
            phone: form.clientPhone.trim(),
            company: form.clientCompany.trim(),
            purchaseDate: new Date().toISOString(),
            serviceId: form.selectedServiceId,
            serviceName: resolvedServiceName,
            status: 'pending',
            notes: form.notes.trim(),
            totalAmount: computed.totalAmount,
            currencyCode: form.currencyCode || 'EUR',
            source: 'manual',
            lastContactAt: '',
          });
        } catch (syncError) {
          console.error(syncError);
          toast.error(
            syncError instanceof Error
              ? `Lien créé, mais client non sync Supabase: ${syncError.message}`
              : 'Lien créé, mais client non sync Supabase.'
          );
        }
      }

      setLastCreatedLink(result.paymentLink);
      resetForm();
      await loadData();

      if (result.emailSent) {
        toast.success('Lien de paiement créé et devis envoyé par email.');
      } else if (result.emailError) {
        toast.success('Lien de paiement créé.');
        toast.error(`Email non envoyé: ${result.emailError}`);
      } else {
        toast.success('Lien de paiement créé.');
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Impossible de créer le lien');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async (paymentLink: AdminPaymentLink) => {
    setIsSendingKey(paymentLink.paymentLinkKey);

    try {
      await adminPaymentLinkService.resendEmail(
        paymentLink.paymentLinkKey,
        paymentLink.emailTemplateAlias || 'checkout-quote'
      );
      await loadData();
      toast.success('Devis renvoyé par email.');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Impossible de renvoyer le devis');
    } finally {
      setIsSendingKey('');
    }
  };

  const handleDeletePaymentLink = async (paymentLink: AdminPaymentLink) => {
    const quoteLabel = paymentLink.quoteNumber || paymentLink.paymentLinkKey;
    if (!window.confirm(`Supprimer le devis ${quoteLabel} ?`)) {
      return;
    }

    setIsDeletingKey(paymentLink.paymentLinkKey);
    try {
      const deleted = await adminPaymentLinkService.removePaymentLink(
        paymentLink.paymentLinkKey
      );
      if (lastCreatedLink?.paymentLinkKey === paymentLink.paymentLinkKey) {
        setLastCreatedLink(null);
      }
      await loadData();
      const cleanupDetails: string[] = [];
      if (deleted.ordersRemoved > 0) {
        cleanupDetails.push(`${deleted.ordersRemoved} commande(s) liée(s)`);
      }
      if (deleted.subscriptionsRemoved > 0) {
        cleanupDetails.push(`${deleted.subscriptionsRemoved} abonnement(s) lié(s)`);
      }
      toast.success(
        cleanupDetails.length > 0
          ? `Devis supprimé (${cleanupDetails.join(', ')}).`
          : 'Devis supprimé.'
      );
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : 'Impossible de supprimer ce devis'
      );
    } finally {
      setIsDeletingKey('');
    }
  };

  const metrics = paymentLinks.reduce(
    (accumulator, paymentLink) => {
      accumulator.total += 1;

      if (['initiated', 'pending'].includes(paymentLink.sessionStatus)) {
        accumulator.awaiting += 1;
      }

      if (paymentLink.sessionStatus === 'paid') {
        accumulator.paid += 1;
      }

      if (paymentLink.emailSentAt) {
        accumulator.emailed += 1;
      }

      return accumulator;
    },
    {
      total: 0,
      awaiting: 0,
      paid: 0,
      emailed: 0,
    }
  );

  const selectableClients = clients;

  return (
    <div className="p-6 space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Paiements, Devis & Abonnements
          </h2>
          <p className="text-gray-600 mt-2">
            Génére des liens de paiement Mollie, crée un devis partageable et envoie le
            lien directement au client. Tu peux aussi ouvrir des abonnements avec
            dates de démarrage, échéances et statut de delivery.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Affichage admin actuel: {displayCurrencyCode}. Les montants d&apos;origine
            des devis restent conservés dans leur devise.
          </p>
        </div>

        <button
          onClick={() => loadData()}
          className="px-4 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors self-start"
        >
          Rafraîchir
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Liens créés</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{metrics.total}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">En attente</div>
          <div className="text-3xl font-bold text-amber-600 mt-2">{metrics.awaiting}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Payés</div>
          <div className="text-3xl font-bold text-emerald-600 mt-2">{metrics.paid}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Emails envoyés</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">{metrics.emailed}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2">
          <button
            type="button"
            onClick={() => setActiveWorkspace('payments')}
            className={`rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
              activeWorkspace === 'payments'
                ? 'bg-neon-orange text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Espace Paiements & Devis
          </button>
          <button
            type="button"
            onClick={() => setActiveWorkspace('subscriptions')}
            className={`rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
              activeWorkspace === 'subscriptions'
                ? 'bg-neon-orange text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Espace Abonnements
          </button>
        </div>
      </div>

      {activeWorkspace === 'payments' && (
      <div className="grid gap-8 xl:grid-cols-[420px_minmax(0,1fr)]">
        <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Nouveau lien de paiement</h3>
            <p className="text-sm text-gray-500 mt-1">
              Prépare un devis, crée le lien Mollie et envoie-le en direct.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client existant
            </label>
            <select
              value={form.selectedClientId}
              onChange={(event) => handleClientChange(event.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
            >
              <option value="">Sélection libre</option>
              {selectableClients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} • {client.email}
                </option>
              ))}
            </select>
            <div className="mt-2 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {selectableClients.length > 0
                  ? `${selectableClients.length} client(s) disponible(s).`
                  : 'Aucun client Supabase charge. Cree un client dans l’onglet Clients puis clique "Rafraichir clients".'}
              </p>
              <button
                type="button"
                onClick={() => {
                  loadClientsOnly()
                    .then((loadedClients) => {
                      if (loadedClients.length > 0) {
                        toast.success(`${loadedClients.length} client(s) recharge(s).`);
                      } else {
                        toast.error('Aucun client trouve pour le moment.');
                      }
                    })
                    .catch((error) => {
                      console.error(error);
                      toast.error(
                        error instanceof Error
                          ? error.message
                          : 'Impossible de recharger les clients'
                      );
                    });
                }}
                className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Rafraichir clients
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom client</label>
              <input
                type="text"
                value={form.clientName}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientName: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={form.clientEmail}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientEmail: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input
                type="text"
                value={form.clientPhone}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientPhone: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
              <input
                type="text"
                value={form.clientCompany}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientCompany: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service catalogué</label>
            <select
              value={form.selectedServiceId}
              onChange={(event) => handleServiceChange(event.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
            >
              <option value="">Personnalisé</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Produit principal (obligatoire)
            </label>
            <input
              type="text"
              value={form.serviceName}
              onChange={(event) =>
                setForm((current) => ({ ...current, serviceName: event.target.value }))
              }
              placeholder="Ex: Plateforme web sur mesure"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix du produit principal
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(event) =>
                  setForm((current) => ({ ...current, amount: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
              {Number(form.amount || 0) > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                  Affichage admin actuel: {formatDisplayAmount(Number(form.amount || 0), form.currencyCode)}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Devise</label>
              <select
                value={form.currencyCode}
                onChange={(event) =>
                  setForm((current) => {
                    const nextCurrencyCode = event.target.value;
                    const selectedService = services.find(
                      (entry) => entry.id === current.selectedServiceId
                    );

                    return {
                      ...current,
                      currencyCode: nextCurrencyCode,
                      amount:
                        selectedService && selectedService.price > 0
                          ? currencyService
                              .convertAmount(selectedService.price, 'EUR', nextCurrencyCode)
                              .toFixed(2)
                          : current.amount,
                    };
                  })
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
              >
                <option value="EUR">EUR</option>
                <option value="CAD">CAD</option>
                <option value="CHF">CHF</option>
                <option value="USD">USD</option>
              </select>
              <div className="mt-2 text-xs text-gray-500">
                Pour un service catalogué, le montant de base EUR est converti automatiquement dans la devise choisie.
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <label className="block text-sm font-medium text-gray-700">
                Upsells / Produits additionnels
              </label>
              <button
                type="button"
                onClick={handleAddUpsellItem}
                className="px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
              >
                + Ajouter un produit
              </button>
            </div>

            <p className="text-xs text-gray-500">
              Ajoute plusieurs lignes (produit, quantité, prix unitaire) pour un vrai panier de paiement.
            </p>

            {form.upsellItems.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                Aucun upsell pour le moment.
              </div>
            ) : (
              <div className="space-y-3">
                {form.upsellItems.map((upsell, index) => {
                  const quantity = Math.max(1, Math.trunc(Number(upsell.quantity || 1)));
                  const unitAmount = Number(upsell.unitAmount || 0);
                  const lineTotal =
                    Number.isFinite(unitAmount) && unitAmount > 0
                      ? Number((unitAmount * quantity).toFixed(2))
                      : 0;

                  return (
                    <div
                      key={upsell.id}
                      className="rounded-xl border border-gray-200 p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                          Upsell {index + 1}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveUpsellItem(upsell.id)}
                          className="px-2 py-1 rounded-md text-xs text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                          Supprimer
                        </button>
                      </div>

                      <div className="grid gap-3 md:grid-cols-6">
                        <div className="md:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Produit
                          </label>
                          <input
                            type="text"
                            value={upsell.label}
                            onChange={(event) =>
                              handleUpsellChange(upsell.id, 'label', event.target.value)
                            }
                            placeholder="Ex: Maintenance mensuelle"
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                          />
                        </div>

                        <div className="md:col-span-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Qté
                          </label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={upsell.quantity}
                            onChange={(event) =>
                              handleUpsellChange(upsell.id, 'quantity', event.target.value)
                            }
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Prix unitaire
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={upsell.unitAmount}
                            onChange={(event) =>
                              handleUpsellChange(upsell.id, 'unitAmount', event.target.value)
                            }
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                          />
                        </div>
                      </div>

                      <div className="text-xs text-gray-500">
                        Sous-total upsell:{' '}
                        {lineTotal > 0
                          ? formatDisplayAmount(lineTotal, form.currencyCode)
                          : '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#f6d8c0] bg-[#fffaf5] p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-gray-900">Panier du devis</div>
              <div className="text-xs text-gray-600">
                {computedDraft.items.length} produit(s)
              </div>
            </div>

            {computedDraft.items.length === 0 ? (
              <p className="text-sm text-gray-500">
                Renseigne le produit principal pour générer un lien de paiement.
              </p>
            ) : (
              <div className="space-y-2">
                {computedDraft.items.map((entry) => (
                  <div
                    key={entry.id || `${entry.label}_${entry.quantity}_${entry.unitAmount}`}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <div className="text-gray-700">
                      <span className="font-medium">{entry.label}</span>
                      <span className="text-gray-500"> x{entry.quantity}</span>
                    </div>
                    <div className="font-semibold text-gray-900">
                      {formatDisplayAmount(entry.lineTotal, form.currencyCode)}
                    </div>
                  </div>
                ))}
                <div className="pt-2 mt-2 border-t border-[#f4e2d3] flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-gray-700">Total à payer</span>
                  <span className="text-base font-bold text-neon-orange">
                    {formatDisplayAmount(computedDraft.totalAmount, form.currencyCode)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro devis
              </label>
              <input
                type="text"
                value={form.quoteNumber}
                onChange={(event) =>
                  setForm((current) => ({ ...current, quoteNumber: event.target.value }))
                }
                placeholder="Auto si vide"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validité
              </label>
              <input
                type="date"
                value={form.quoteValidUntil}
                onChange={(event) =>
                  setForm((current) => ({ ...current, quoteValidUntil: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message visible pour le client
            </label>
            <textarea
              value={form.clientMessage}
              onChange={(event) =>
                setForm((current) => ({ ...current, clientMessage: event.target.value }))
              }
              rows={4}
              placeholder="Ex: Ce devis inclut la mise en ligne, le cadrage et le suivi initial."
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes internes
            </label>
            <textarea
              value={form.notes}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
              rows={3}
              placeholder="Visible seulement dans l’admin."
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
            />
          </div>

          <label className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3 border border-gray-200">
            <input
              type="checkbox"
              checked={form.sendEmail}
              onChange={(event) =>
                setForm((current) => ({ ...current, sendEmail: event.target.checked }))
              }
            />
            <span className="text-sm text-gray-700">
              Envoyer le devis par email dès la création
            </span>
          </label>

          <div className="flex gap-3">
            <button
              onClick={handleCreate}
              disabled={isSubmitting}
              className="flex-1 px-5 py-3 rounded-xl bg-neon-orange text-white hover:bg-[#FF4500] transition-colors disabled:opacity-60"
            >
              {isSubmitting ? 'Création...' : 'Créer le lien de paiement'}
            </button>
            <button
              onClick={resetForm}
              className="px-5 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </section>

        <section className="space-y-6">
          {lastCreatedLink && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-emerald-700">
                    Dernier lien créé
                  </div>
                  <div className="text-lg font-bold text-gray-900 mt-1">
                    {lastCreatedLink.quoteNumber} • {lastCreatedLink.serviceName}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {lastCreatedLink.clientName} •{' '}
                    {formatDisplayAmount(lastCreatedLink.amount, lastCreatedLink.currencyCode)}
                    {lastCreatedLink.currencyCode !== displayCurrencyCode && (
                      <span className="block text-xs text-gray-500 mt-1">
                        Origine: {formatOriginalAmount(lastCreatedLink.amount, lastCreatedLink.currencyCode)}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Type: {PAYMENT_MODE_LABELS[lastCreatedLink.paymentMode]}
                    {lastCreatedLink.paymentMode === 'recurring' && lastCreatedLink.recurringStatus
                      ? ` • statut: ${lastCreatedLink.recurringStatus}`
                      : ''}
                  </div>
                  {lastCreatedLink.lineItems.length > 0 && (
                    <div className="text-xs text-gray-500 mt-2">
                      Panier: {formatLineItemsSummary(lastCreatedLink.lineItems)}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <a
                    href={lastCreatedLink.paymentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-lg bg-white text-gray-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                  >
                    Ouvrir paiement
                  </a>
                  <button
                    onClick={() => copyToClipboard(lastCreatedLink.paymentUrl, 'Lien de paiement')}
                    className="px-4 py-2 rounded-lg bg-white text-gray-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                  >
                    Copier paiement
                  </button>
                  <button
                    onClick={() => copyToClipboard(lastCreatedLink.quoteUrl, 'Lien devis')}
                    className="px-4 py-2 rounded-lg bg-white text-gray-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                  >
                    Copier devis
                  </button>
                  <a
                    href={lastCreatedLink.quoteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                  >
                    Ouvrir le devis
                  </a>
                  <a
                    href={adminPaymentLinkService.getInvoicePdfUrl(lastCreatedLink.paymentLinkKey)}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-lg bg-white text-gray-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                  >
                    Facture PDF
                  </a>
                  <button
                    onClick={() => handleDeletePaymentLink(lastCreatedLink)}
                    disabled={isDeletingKey === lastCreatedLink.paymentLinkKey}
                    className="px-4 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-60"
                  >
                    {isDeletingKey === lastCreatedLink.paymentLinkKey
                      ? 'Suppression...'
                      : 'Supprimer'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Historique des devis</h3>
              <p className="text-sm text-gray-500 mt-1">
                Suis les liens envoyés, leur statut et renvoie le devis si besoin.
              </p>
            </div>

            {isLoading ? (
              <div className="py-16 text-center text-gray-600">Chargement des liens...</div>
            ) : paymentLinks.length === 0 ? (
              <div className="py-16 text-center text-gray-600">
                Aucun lien de paiement généré pour le moment.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {paymentLinks.map((paymentLink, index) => (
                  <motion.div
                    key={paymentLink.paymentLinkKey}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-6"
                  >
                    <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
                      <div className="space-y-3 min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {paymentLink.quoteNumber}
                          </h4>
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${STATUS_BADGES[paymentLink.sessionStatus]}`}
                          >
                            {STATUS_LABELS[paymentLink.sessionStatus]}
                          </span>
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${PAYMENT_MODE_BADGES[paymentLink.paymentMode]}`}
                          >
                            {PAYMENT_MODE_LABELS[paymentLink.paymentMode]}
                          </span>
                        </div>

                        <div className="text-gray-900 font-medium">{paymentLink.serviceName}</div>
                        {paymentLink.lineItems.length > 0 && (
                          <div className="text-xs text-gray-500">
                            Panier: {formatLineItemsSummary(paymentLink.lineItems)}
                          </div>
                        )}
                        <div className="text-sm text-gray-600">
                          {paymentLink.clientName} • {paymentLink.clientEmail}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDisplayAmount(paymentLink.amount, paymentLink.currencyCode)} •
                          {' '}validité {formatDate(paymentLink.quoteValidUntil)}
                          {paymentLink.currencyCode !== displayCurrencyCode && (
                            <span className="block text-xs text-gray-400 mt-1">
                              Origine: {formatOriginalAmount(paymentLink.amount, paymentLink.currencyCode)}
                            </span>
                          )}
                        </div>

                        {paymentLink.notes && (
                          <div className="text-sm text-gray-500">{paymentLink.notes}</div>
                        )}

                        <div className="grid gap-2 text-xs text-gray-500 md:grid-cols-2">
                          <div>Créé le {formatDateTime(paymentLink.createdAt)}</div>
                          <div>Email: {paymentLink.emailSentAt ? formatDateTime(paymentLink.emailSentAt) : 'non envoyé'}</div>
                          <div>Dernier statut fournisseur: {paymentLink.lastStatus || 'open'}</div>
                          <div>Paiement: {paymentLink.paidAt ? formatDateTime(paymentLink.paidAt) : 'pas encore'}</div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 xl:justify-end">
                        <a
                          href={paymentLink.paymentUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-4 py-2 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                        >
                          Ouvrir paiement
                        </a>
                        <button
                          onClick={() => copyToClipboard(paymentLink.paymentUrl, 'Lien de paiement')}
                          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Copier paiement
                        </button>
                        <button
                          onClick={() => copyToClipboard(paymentLink.quoteUrl, 'Lien devis')}
                          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Copier devis
                        </button>
                        <a
                          href={paymentLink.quoteUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-4 py-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        >
                          Ouvrir devis
                        </a>
                        <a
                          href={adminPaymentLinkService.getInvoicePdfUrl(paymentLink.paymentLinkKey)}
                          target="_blank"
                          rel="noreferrer"
                          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Facture PDF
                        </a>
                        <button
                          onClick={() => handleResendEmail(paymentLink)}
                          disabled={
                            isSendingKey === paymentLink.paymentLinkKey ||
                            isDeletingKey === paymentLink.paymentLinkKey
                          }
                          className="px-4 py-2 rounded-lg bg-neon-orange text-white hover:bg-[#FF4500] transition-colors disabled:opacity-60"
                        >
                          {isSendingKey === paymentLink.paymentLinkKey
                            ? 'Envoi...'
                            : 'Renvoyer email'}
                        </button>
                        <button
                          onClick={() => handleDeletePaymentLink(paymentLink)}
                          disabled={isDeletingKey === paymentLink.paymentLinkKey}
                          className="px-4 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-60"
                        >
                          {isDeletingKey === paymentLink.paymentLinkKey
                            ? 'Suppression...'
                            : 'Supprimer'}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
      )}

      {activeWorkspace === 'subscriptions' && (
        <PaymentSubscriptionsPanel
          clients={clients}
          services={services}
          paymentLinks={paymentLinks}
          displayCountry={displayCountry}
        />
      )}
    </div>
  );
};

export default PaymentLinksManager;
